import logger from "../utils/logger";
import { withTimeout } from "../utils/withTimeout";

const VERSION_MANIFEST_URL = "/version.json";
const MANIFEST_CACHE_TTL_MS = 30000;
const VERSION_MANIFEST_TIMEOUT_MS = 3500;

let manifestCache = null;

const toVersionParts = (version) => {
  if (!version) {
    return [0];
  }

  return String(version)
    .split(".")
    .map((part) => Number.parseInt(part, 10) || 0);
};

const compareVersions = (leftVersion, rightVersion) => {
  const leftParts = toVersionParts(leftVersion);
  const rightParts = toVersionParts(rightVersion);
  const length = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < length; index += 1) {
    const leftPart = leftParts[index] || 0;
    const rightPart = rightParts[index] || 0;

    if (leftPart > rightPart) {
      return 1;
    }

    if (leftPart < rightPart) {
      return -1;
    }
  }

  return 0;
};

const buildDegradedState = () => ({
  isSupported: true,
  forceRefresh: false,
  manifest: null,
});

const isValidManifest = (manifest) => {
  return (
    manifest &&
    typeof manifest === "object" &&
    typeof manifest.version === "string" &&
    manifest.version.length > 0 &&
    typeof manifest.buildTime === "string" &&
    manifest.buildTime.length > 0 &&
    typeof manifest.min_supported_version === "string" &&
    manifest.min_supported_version.length > 0 &&
    Number.isInteger(manifest.schema_epoch) &&
    typeof manifest.force_refresh === "boolean"
  );
};

const normalizeManifest = (manifest) => ({
  version: manifest.version,
  buildTime: manifest.buildTime,
  min_supported_version: manifest.min_supported_version,
  schema_epoch: manifest.schema_epoch,
  force_refresh: manifest.force_refresh,
});

export const fetchVersionManifest = async () => {
  const now = Date.now();

  if (manifestCache && now - manifestCache.fetchedAt < MANIFEST_CACHE_TTL_MS) {
    return manifestCache.data;
  }

  let timeoutId = null;
  let controller = null;

  if (typeof AbortController !== "undefined") {
    controller = new AbortController();
    timeoutId = globalThis.setTimeout(() => {
      controller.abort();
    }, VERSION_MANIFEST_TIMEOUT_MS);
  }

  const response = await withTimeout(
    fetch(VERSION_MANIFEST_URL, {
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
      signal: controller?.signal,
    }),
    VERSION_MANIFEST_TIMEOUT_MS,
    "Version manifest request timed out",
  ).finally(() => {
    if (timeoutId !== null) {
      globalThis.clearTimeout(timeoutId);
    }
  });

  if (!response.ok) {
    throw new Error(`Version manifest request failed with status ${response.status}`);
  }

  const manifestJson = await response.json();

  if (!isValidManifest(manifestJson)) {
    throw new Error("Version manifest is invalid");
  }

  const manifest = normalizeManifest(manifestJson);

  manifestCache = {
    data: manifest,
    fetchedAt: now,
  };

  return manifest;
};

export const getCompatibilityState = async (currentVersion) => {
  try {
    const manifest = await fetchVersionManifest();
    const minimumSupportedVersion = manifest.min_supported_version;
    const isSupported =
      !minimumSupportedVersion ||
      compareVersions(currentVersion, minimumSupportedVersion) >= 0;

    return {
      isSupported,
      forceRefresh: manifest.force_refresh,
      manifest,
    };
  } catch (error) {
    logger.warn("Failed to load version manifest", error);
    return buildDegradedState();
  }
};
